import React, { useEffect, useRef, useState } from 'react';
import Heading from '../../Atoms/Heading/Heading';
import { dismissBannerAnalytics, getDaysInYear, getStoreIDByURL, toastBannerAnalytics } from '../../../Utils/componentUtils';
import './Menu.scss';
import { MenuProps } from '../../../Interfaces/MenuProps';
import Modal from '../Modal/Modal';
import Button from '../../Atoms/Button/Button';
import ErrorModal from '../CartModal/ErrorModal';
import useStore from '../../../store/useStore';
import useRequest from '../../../hooks/useRequest';
import Skeleton from '../../Templates/Skeleton/Skeleton';
import { setMaxAttributes } from '../../../Utils/OrderViewModelUtil';
import { getUtcTime } from '../../../Utils/pageUtils';
import SodiumWarning from '../SodiumWarning/SodiumWarning';
import { multipleAndCheck } from '../../../Utils/WindowUtils';
import MenuCardList from './MenuCardList';
import { MENU_CLICK } from '../../../constants/AnalyticsConstants';

/**
 * Menu component
 * @param Title heading text, level h1
 * @param description description paragraph
 * @param apiRoot API endpoint to get data for slides
 * @returns Reusable menu component
 */
const Menu: React.FC<MenuProps> = ({
  description,
  apiEndpoint,
  siteName,
  dataSource,
  params,
  okText,
  noResults,
  orderViewModel,
  subwayScript,
}) => {
  const [showModal, setshowModal] = useState<boolean>(false);
  const [showSodiumWarning, setShowSodiumWarning] = useState<boolean>(false);
  const isBannerAlreadyOpen = useRef(false);
  const {
    setPageLoadAnalytics,
    pageLoadAnalytics,
    noOFComponents,
    setAnalyticsProducts,
    setAttemptTracker,
    setCategories,
  } = useStore();
  // useAxios hook to get data from API
  const {
    data: menuResponse,
    isLoading,
    error,
  } = useRequest({
    params: {
      RestaurantId: getStoreIDByURL(),
      DatasourceId: dataSource,
      sitename: siteName,
      apiEndpoint: apiEndpoint.value,
      cacheDuration: params.CacheDuration,
      userLocalTime: getUtcTime(),
    },
  });
  const handleSodiumWarning = () => {
    setShowSodiumWarning(false);
  }

  const onAnchorClick = (e: React.SyntheticEvent, card: any) => {
    e.stopPropagation();
    setAnalyticsProducts({
      clickActionName: card.name,
      productCategory : card.name,
    });
    setAttemptTracker([{ name: MENU_CLICK }]);
  }

  useEffect(() => {
    if (menuResponse && pageLoadAnalytics === 0 && noOFComponents.length === 1) {
      setPageLoadAnalytics(pageLoadAnalytics + 1);
    }
    if (menuResponse && pageLoadAnalytics === 1 && noOFComponents.length === 2) {
      setPageLoadAnalytics(pageLoadAnalytics + 1);
    }
    if (menuResponse && pageLoadAnalytics === 2 && noOFComponents.length === 3) {
      setPageLoadAnalytics(pageLoadAnalytics + 1);
    }
    setMaxAttributes(menuResponse);
    setCategories(menuResponse?.categories || []);
    if (multipleAndCheck(menuResponse, menuResponse?.storeCloseThreshold, !isBannerAlreadyOpen.current)) {
      isBannerAlreadyOpen.current = true;
      (window as any).getBannerMessage(
        'error',
        menuResponse.storeCloseDictionary,
        false,
        false,
        null,
        () => { dismissBannerAnalytics(setAnalyticsProducts, setAttemptTracker, true) },
        null,
        '.header-sticky',
        'gray',
      );
      toastBannerAnalytics(setAnalyticsProducts, setAttemptTracker, menuResponse.storeCloseDictionary);
    }
    if (multipleAndCheck(menuResponse?.isSodiumWarning, (subwayScript.getCookie('sodiumWarning') === '' ))) {
      subwayScript.setCookie('sodiumWarning', 'true', getDaysInYear());
      setShowSodiumWarning(true);
    }
  }, [pageLoadAnalytics, menuResponse, subwayScript]);

  const { error: menuError, dictionaryLables, categories, noResultsMessage } = menuResponse || {};
  useEffect(() => {
    if (menuError) {
      setshowModal(true);
    }
  }, [menuError]);

  if (menuError) {
    return (
      <>
        <Heading level="p" className="menu__noresults" title={noResults?.value} />
        {showModal && (
          <>
            {orderViewModel.currentOrder.cartId && (
              <Modal
                data-testauto-id="menu"
                isShown={showModal}
                title={menuError.title}
                onButtonClick={() => {
                  setAnalyticsProducts({});
                  setshowModal(false);
                }}
                description={menuError.messageBody}
                isClosebtn={true}
              >
                <Button
                  testAutoId={'menu-oktext-btn'}
                  className="popup__button"
                  onClick={() => setshowModal(false)}
                >
                  {okText}
                </Button>
              </Modal>
            )}
          </>
        )}
      </>
    );
  }
  return (
      <div className="menu_main">
        {isLoading && (
            <Skeleton count={6} className={'menu'} isDescription={false} flexContent={'row'}/>
        )}

        {noResultsMessage && (
          <Heading level="p" className="menu__noresults" title={noResultsMessage} />
        )}
        {error && orderViewModel.currentOrder.cartId && (
          <ErrorModal error={error} okText={okText} value={'menu'} />
        )}
        <section className="menu__container">
          {dictionaryLables && categories && categories.length > 0 && (
            <>
              <div className="menu__groupName">
                <div>
                  {dictionaryLables && (
                    <Heading
                      level="h1"
                      className="menu__title"
                      testAutoId='menu-main-heading'
                      title={dictionaryLables.menuTitle}
                    />
                  )}
                  {description && <Heading level="p" className="menu__desc" title={description} />}
                </div>
              </div>
              <MenuCardList cardListData={categories} sourceComponent='menu' onAnchorClick={onAnchorClick} />
            </>
          )}
        </section>
        {showSodiumWarning && (
        <SodiumWarning
        subwayScript={subwayScript}
        sodiumWarningTitle={menuResponse.sodiumWarningTitle}
        sodiumWarning={menuResponse.sodiumWarning}
        okText={menuResponse.okText}
        moreInfoUrl={menuResponse.moreInfoUrl}
        moreInfoText={menuResponse.moreInfo}
        handleSodiumWarning={handleSodiumWarning}
        showModal={showSodiumWarning}
        />
      )}
      </div>
  );
};
export default Menu;
